import { useState, useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { useDashboard } from "../context/DashboardContext";
import { fetchClientDetail, fetchSocialOverview } from "../api";
import { LoadingState } from "../components/LoadingState";
import { SocialTab } from "../components/social/SocialTab";
import type { SocialDetailPayload, GertrudisAuditPayload } from "../types";
import { readStoredRange, rangeToQuery, storeRange, type SocialRange } from "../components/social/socialRange";

export function SocialPage() {
  const {
    data: dashboardData,
    session,
    selectedClient,
    setSelectedClient,
    loading: dashboardLoading,
  } = useDashboard();

  const [socialData, setSocialData] = useState<SocialDetailPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<SocialRange>(readStoredRange);

  const handleRangeChange = (next: SocialRange) => {
    setRange(next);
    storeRange(next);
  };

  const effectiveClient =
    selectedClient && selectedClient !== "ALL"
      ? selectedClient
      : (session?.tenantSlug || dashboardData?.clients?.[0]?.clientSlug || "chp");

  useEffect(() => {
    if (!effectiveClient) return;
    let active = true;
    setLoading(true);
    setError(null);

    // El resumen se pide a la vez que el detalle: cuando el detalle llega, el resumen ya viene en camino.
    fetchSocialOverview(effectiveClient, "all", rangeToQuery(readStoredRange())).catch(() => undefined);

    fetchClientDetail(effectiveClient, "social")
      .then((res) => {
        if (active) setSocialData(res as SocialDetailPayload);
      })
      .catch((err) => {
        if (active) {
          console.error("Error al cargar Social:", err);
          setError(err instanceof Error ? err.message : "Error al cargar datos de Social.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [effectiveClient]);

  const handleAuditGenerated = (audit: GertrudisAuditPayload) => {
    setSocialData((curr) => {
      if (!curr) return curr;
      const history = curr.audit?.history ? [audit, ...curr.audit.history] : [audit];
      return { ...curr, audit: { ...audit, history } };
    });
  };

  if (selectedClient === "ALL" && dashboardLoading && !dashboardData) return <LoadingState />;

  if (selectedClient === "ALL") {
    return (
      <div className="page" style={{ padding: "32px 24px" }}>
        <section className="empty-selection">
          <span className="empty-selection-mark">W</span>
          <h1>Elige un cliente para abrir su Reputation Desk</h1>
          <p>El selector del header persistirá cuando navegues por otros módulos.</p>
          <div className="client-picker-grid">
            {dashboardData?.clients.map((item) => (
              <button
                type="button"
                key={item.clientSlug}
                onClick={() => setSelectedClient(item.clientSlug)}
              >
                <span>{item.displayName}</span>
                <small>{item.alertCount} alertas</small>
                <ArrowUpRight size={15} />
              </button>
            ))}
          </div>
        </section>
      </div>
    );
  }

  if (loading && !socialData) return <LoadingState />;

  if (error && !socialData) {
    return (
      <div className="page" style={{ padding: "24px" }}>
        <div className="detail-notice is-danger">{error}</div>
      </div>
    );
  }

  if (!socialData) return null;

  return (
    <div className="social-page-wrap" style={{ width: "100%", minHeight: "100vh" }}>
      <SocialTab
        data={socialData}
        clientSlug={effectiveClient}
        onAuditGenerated={handleAuditGenerated}
        range={range}
        onRangeChange={handleRangeChange}
        portalControls={true}
        showHeader={false}
      />
    </div>
  );
}
